import { useQueryClient } from '@tanstack/react-query'
import { type MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { SearchAlbumsCombobox } from '@components/app/panels/right-panel/combo-box'
import { usePhotoAlbumsQuery } from '@/api/hooks/use-photo-albums'
import { queryKeys } from '@/api/query-keys'
import type { PhotoCandid } from '@/api/types/photo'
import {
  TrayGrid,
  TrayPanelLayout,
  useAnyDropdownOpen,
} from '@components/app/panels/right-panel/tray'
import { PhotoTrayThumbnail } from '@/components/app/photos/photo-tray-thumbnail'
import { useTrayLazyWindow } from '@/hooks/use-tray-lazy-window'
import { decodeAlbumSelection } from '@/lib/photos/album-selection'
import { PHOTOS_TRAY_SORT_OPTIONS } from '@/lib/photos/sort-tray-photos'
import { trayColumnCount } from '@/lib/tray/tray-lazy-window'
import { FavoritesErrorDialog } from './components/favorites-error-dialog'
import { PhotosTrayFilters } from './components/photos-tray-filters'
import { PhotosTrayStatus } from './components/photos-tray-status'
import { useAlbumPhotosPlacementRefresh } from './hooks/use-album-photos-placement-refresh'
import { useFavoritesErrorDialog } from './hooks/use-favorites-error-dialog'
import { usePhotosTrayAlbum } from './hooks/use-photos-tray-album'
import { usePhotosTrayFilters } from './hooks/use-photos-tray-filters'
import { usePhotosTrayPhotos } from './hooks/use-photos-tray-photos'
import { usePhotosTrayViewOptions } from './hooks/use-photos-tray-view-options'

export type PhotosPanelEscapeStateRef = MutableRefObject<{
  filtersExpanded: boolean
  collapseFilters: () => void
}>

export type PhotosPanelProps = {
  spreadId?: string | null
  /** Album / topics / tags / sort popovers and tray hover previews — Escape dismisses these first. */
  onAnyDropdownOpenChange?: (open: boolean) => void
  /** Live filters-expanded state for the host Escape chain (WMPCM-11088). */
  escapeStateRef?: PhotosPanelEscapeStateRef
}

export function PhotosPanel({
  spreadId = null,
  onAnyDropdownOpenChange,
  escapeStateRef,
}: PhotosPanelProps) {
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filters = usePhotosTrayFilters()

  const reportAnyDropdownOpenChange = useAnyDropdownOpen(
    { album: false, topics: false, tags: false, sort: false, preview: false },
    onAnyDropdownOpenChange
  )

  const reportPreviewOpenChange = useMemo(
    () => reportAnyDropdownOpenChange('preview'),
    [reportAnyDropdownOpenChange]
  )

  useEffect(() => {
    if (!escapeStateRef) return

    escapeStateRef.current = {
      filtersExpanded: filtersOpen,
      collapseFilters: () => setFiltersOpen(false),
    }
  }, [escapeStateRef, filtersOpen])

  const { albumValue, handleAlbumChange } = usePhotosTrayAlbum(spreadId)

  const { showFileName, handleShowFileNameChange, traySort, handleTraySortChange } =
    usePhotosTrayViewOptions({ albumValue, spreadId, placedFilter: filters.placedFilter })

  const photos = usePhotosTrayPhotos({
    albumValue,
    spreadId,
    filenameQuery: filters.filenameQuery,
    isFilenameSearch: filters.isFilenameSearch,
    trayFilters: filters.trayFilters,
    traySort,
  })

  const resultCount = photos.displayPhotos.length
  const {
    visibleItems: visiblePhotos,
    hasMore: hasMoreVisible,
    columns: columnCount,
    sentinelRef: loadMoreSentinelRef,
  } = useTrayLazyWindow({
    items: photos.displayPhotos,
    scrollRootRef,
    // Any control change collapses the lazy window back to its first chunk.
    resetKey: JSON.stringify([
      albumValue,
      filters.filenameQuery,
      filters.favoritedFilter,
      filters.orientationFilter,
      filters.placedFilter,
      filters.selectedStudentIds,
      filters.selectedTopicTagIds,
      showFileName,
      filters.taggedFilter,
      traySort,
    ]),
  })

  useAlbumPhotosPlacementRefresh(albumValue, spreadId)

  const { data: photoAlbums = [] } = usePhotoAlbumsQuery({
    enabled: photos.isTrayLoading && Boolean(albumValue) && !filters.isFilenameSearch,
  })

  const skeletonColumns = useMemo((): 1 | 2 => {
    if (!photos.isTrayLoading) return 2

    let total: number | null = null

    if (filters.isFilenameSearch) {
      const cached = queryClient.getQueryData<PhotoCandid[]>(
        queryKeys.photos.searchPhotos(filters.filenameQuery)
      )

      total = cached?.length ? cached.length : null
    } else {
      const selection = decodeAlbumSelection(albumValue)

      if (selection?.type === 'album') {
        const album = photoAlbums.find((item) => item.albumId === selection.albumId)

        if (typeof album?.candidCount === 'number') {
          total = album.candidCount
        }
      }

      if (total === null) {
        const cached = queryClient.getQueryData<PhotoCandid[]>(
          queryKeys.photos.albumPhotos(albumValue, spreadId)
        )

        total = cached?.length ? cached.length : null
      }
    }

    return total === null ? 1 : trayColumnCount(total)
  }, [
    albumValue,
    filters.filenameQuery,
    filters.isFilenameSearch,
    photoAlbums,
    photos.isTrayLoading,
    queryClient,
    spreadId,
  ])

  const [favoritesErrorOpen, setFavoritesErrorOpen] = useFavoritesErrorDialog({
    isFavorites: photos.isFavorites,
    isPhotosError: photos.isPhotosError,
    isPhotosLoading: photos.isPhotosLoading,
    albumPhotoCount: photos.albumPhotos.length,
  })

  return (
    <>
      <TrayPanelLayout
        scrollRootRef={scrollRootRef}
        picker={
          <SearchAlbumsCombobox
            value={albumValue}
            onChange={handleAlbumChange}
            spreadId={spreadId}
            placeholder="Select album"
            onOpenChange={reportAnyDropdownOpenChange('album')}
          />
        }
        filtersOpen={filtersOpen}
        onFiltersOpenChange={setFiltersOpen}
        activeFilterCount={filters.activeFilterCount}
        onClearFilters={filters.handleClearFilters}
        filters={
          <PhotosTrayFilters
            filters={filters}
            onTopicsOpenChange={reportAnyDropdownOpenChange('topics')}
            onTagsOpenChange={reportAnyDropdownOpenChange('tags')}
          />
        }
        separatorAfterFilters
        showFilename={showFileName}
        onShowFilenameChange={handleShowFileNameChange}
        resultCount={resultCount}
        showResultCount={photos.canShowResults}
        sort={{
          value: traySort,
          options: PHOTOS_TRAY_SORT_OPTIONS,
          onValueChange: handleTraySortChange,
          ariaLabel: 'Sort photos',
          listboxLabel: 'Sort photos by',
          onOpenChange: reportAnyDropdownOpenChange('sort'),
        }}
      >
        <PhotosTrayStatus
          spreadId={spreadId}
          canShowTray={photos.canShowTray}
          canShowResults={photos.canShowResults}
          hasAlbumSelected={photos.hasAlbumSelected}
          isFilenameSearch={filters.isFilenameSearch}
          isRecommendForSpread={photos.isRecommendForSpread}
          isFavorites={photos.isFavorites}
          isTrayLoading={photos.isTrayLoading}
          isTrayError={photos.isTrayError}
          resultCount={resultCount}
          skeletonColumns={skeletonColumns}
        />

        <TrayGrid
          columns={columnCount}
          sentinelRef={loadMoreSentinelRef}
          showSentinel={photos.canShowResults && hasMoreVisible}
        >
          {photos.canShowResults &&
            visiblePhotos.map((photo) => (
              <PhotoTrayThumbnail
                key={photo.candidId}
                photo={photo}
                columns={columnCount}
                showFileName={showFileName}
                showRemove={photos.isRecommendToMe}
                onRemove={() => photos.removeRecommendation.mutate(photo.candidId)}
                removeDisabled={photos.removeRecommendation.isPending}
                onPreviewOpenChange={reportPreviewOpenChange}
              />
            ))}
        </TrayGrid>
      </TrayPanelLayout>

      <FavoritesErrorDialog
        open={favoritesErrorOpen}
        onOpenChange={setFavoritesErrorOpen}
        error={photos.photosError}
      />
    </>
  )
}
